import { useTranslation } from 'react-i18next';
import { Controller, type Control, type FieldValues } from 'react-hook-form';
import type { PricesUpdate } from '@baerenfang/shared';
import { chf } from '../lib/format';

export const CATEGORIES = ['members_adult', 'members_child', 'guests_adult', 'guests_child'] as const;
export type Category = (typeof CATEGORIES)[number];

// `Control` is invariant in its TFieldValues parameter, so the two
// concrete shapes (StayFormValues and ExternalFormValues) can't both be
// passed without widening. Using FieldValues (the library's
// Record<string, any>) lets either page's Control flow in unchanged.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyControl = Control<FieldValues>;

type Props = {
  index: number;
  control: AnyControl;
  variant: 'table' | 'card';
  prices: PricesUpdate;
  lang: string;
};

// Fully controlled via Controller — the previous uncontrolled `register` path
// combined with type="number"+valueAsNumber+default 0 had cross-browser quirks
// that silently swallowed typed digits.
function NightCell({
  control,
  index,
  cat,
}: {
  control: AnyControl;
  index: number;
  cat: Category;
}) {
  const { t } = useTranslation();
  return (
    <Controller
      name={`nights.${index}.${cat}`}
      control={control}
      render={({ field }) => (
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete="off"
          aria-label={t(`stayForm.cat.${cat}`)}
          value={field.value ?? ''}
          onChange={(e) => {
            const raw = e.target.value.replace(/[^0-9]/g, '');
            field.onChange(raw === '' ? 0 : Number.parseInt(raw, 10));
          }}
          onBlur={field.onBlur}
          onFocus={(e) => e.currentTarget.select()}
          className="rounded-md border border-slate-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 text-sm py-2 px-2 w-full text-center focus:outline-none focus:ring-2 focus:ring-slate-500 dark:focus:ring-slate-400"
        />
      )}
    />
  );
}

export function NightInputs({ index, control, variant, prices, lang }: Props) {
  const { t } = useTranslation();
  if (variant === 'table') {
    return (
      <>
        {CATEGORIES.map((cat) => (
          <td key={cat} className="p-1">
            <NightCell control={control} index={index} cat={cat} />
          </td>
        ))}
      </>
    );
  }
  // Each cell is a 2-row grid: label/price area (1fr) on top, input (auto) at
  // the bottom. The outer grid equalises row heights, so within any row the
  // inputs line up — even when one label wraps ("(unter 16)") and another
  // does not. Across rows the input y-position may differ, which is fine.
  return (
    <div className="grid grid-cols-2 gap-2">
      {CATEGORIES.map((cat) => (
        <label key={cat} className="grid grid-rows-[1fr_auto] gap-1 text-sm">
          <span className="block leading-tight">
            <span className="text-slate-700 dark:text-slate-300">{t(`stayForm.cat.${cat}`)}</span>
            <span className="block text-xs text-slate-500 dark:text-slate-400 font-normal tabular-nums">
              {chf(prices[cat], lang)}
            </span>
          </span>
          <NightCell control={control} index={index} cat={cat} />
        </label>
      ))}
    </div>
  );
}
